import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

const lightPalette = {
  name: 'light',
  backgroundColor: '#F7F7F8',
  text: '#1f1f1f',
  secondaryText: '#2e2e2e',
  mutedText: 'rgba(0,0,0,0.6)',
  lightText: 'rgba(0,0,0,0.3)',
  primary: 'rgb(255, 71, 71)',
  surface: 'rgba(255,255,255,0.8)',
  contrastText: '#000000',
  lightTextSecondary: 'rgba(74, 71, 70, 0.62)',
  border: 'rgba(0,0,0,0.12)',
  surfaceSolid: '#F7F7F8',
  white: '#F1F1F1',
  retroCyan: '#18B3B7'

};

const darkPalette = {
  name: 'dark',
  backgroundColor: '#080808',
  text: '#FFFFFF',
  contrastText: '#FFFFFF',
  secondaryText: '#E4DAD7',
  mutedText: 'rgba(255,255,255,0.7)',
  lightText: 'rgba(255,255,255,0.3)',
  lightTextSecondary: 'rgba(235,213,208,0.62)',
  primary: 'rgb(255, 71, 71)',
  surface: 'rgba(23, 23, 23, 0.8)',
  surfaceSolid: '#171717',
  border: 'rgba(255,255,255,0.18)'
};

// Helper function to determine theme based on time (dark 5PM-5AM, light 5AM-5PM)
const getTimeBasedMode = () => {
  const now = new Date();
  const hour = now.getHours();
  // Dark mode: 5PM (17:00) to 5AM (05:00)
  // Light mode: 5AM (05:00) to 5PM (17:00)
  const mode = hour >= 17 || hour < 5 ? 'dark' : 'light';
  return mode;
};

// LocalStorage helpers for theme persistence
const STORAGE_KEYS = {
  MODE: 'theme_mode',
  AUTO_UPDATE: 'theme_autoUpdate'
};

const getStoredMode = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.MODE);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch (error) {
    console.error('[ThemeContext] Error reading mode from localStorage:', error);
    return null;
  }
};

const getStoredAutoUpdate = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTO_UPDATE);
    return stored !== null ? stored === 'true' : null;
  } catch (error) {
    console.error('[ThemeContext] Error reading autoUpdate from localStorage:', error);
    return null;
  }
};

const saveModeToStorage = (mode) => {
  try {
    localStorage.setItem(STORAGE_KEYS.MODE, mode);
  } catch (error) {
    console.error('[ThemeContext] Error saving mode to localStorage:', error);
  }
};

const saveAutoUpdateToStorage = (autoUpdate) => {
  try {
    localStorage.setItem(STORAGE_KEYS.AUTO_UPDATE, String(autoUpdate));
  } catch (error) {
    console.error('[ThemeContext] Error saving autoUpdate to localStorage:', error);
  }
};

export function ThemeProvider({ children }) {
  const { user, updateUserPreferences } = useAuth();
  
  // Initialize state from localStorage or defaults
  const [mode, setMode] = useState(() => {
    const stored = getStoredMode();
    return stored || 'dark';
  });
  const [autoUpdate, setAutoUpdate] = useState(() => {
    const stored = getStoredAutoUpdate();
    return stored !== null ? stored : true;
  });
  const intervalRef = useRef(null);

  // Load user preferences when user logs in, or use localStorage values
  useEffect(() => {
    if (user && user.preferences) {
      const userMode = user.preferences.darkMode ? 'dark' : 'light';
      const userAutoUpdate = user.preferences.autoUpdate !== undefined ? user.preferences.autoUpdate : true;
      
      setMode(userMode);
      setAutoUpdate(userAutoUpdate);
      
      // Also save to localStorage as backup
      saveModeToStorage(userMode);
      saveAutoUpdateToStorage(userAutoUpdate);
    } else if (!user) {
      // No user - use localStorage values (already loaded in useState initializer)
      const storedMode = getStoredMode();
      const storedAutoUpdate = getStoredAutoUpdate();
      
      if (storedMode) {
        setMode(storedMode);
      }
      if (storedAutoUpdate !== null) {
        setAutoUpdate(storedAutoUpdate);
      }
    }
  }, [user?.uid]); // Only run when user ID changes, not on every user object update

  // Determine mode based on user preferences
  useEffect(() => {
    if (autoUpdate) {
      // Time-based mode: update based on time of day
      const updateTimeBasedMode = () => {
        const timeBasedMode = getTimeBasedMode();
        setMode(timeBasedMode);

        // Save to localStorage
        saveModeToStorage(timeBasedMode);

        if (user && updateUserPreferences) {
          // Update Firestore with time-based mode
          updateUserPreferences({
            ...user.preferences,
            darkMode: timeBasedMode === 'dark',
            autoUpdate: true,
          }).catch(err => {
            console.error('[ThemeContext] Failed to update time-based preferences:', err);
          });
        }
      };

      // Set initial mode
      updateTimeBasedMode();

      // Update every minute to catch transitions
      intervalRef.current = setInterval(updateTimeBasedMode, 60000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Manual mode: keep current mode, don't update it
      // Just clear the interval if it was set
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [autoUpdate, user?.uid]); // Removed 'mode' from dependencies to prevent loops

  const palette = mode === 'dark' ? darkPalette : lightPalette;

  // Save to localStorage whenever mode or autoUpdate changes
  useEffect(() => {
    saveModeToStorage(mode);
  }, [mode]);

  useEffect(() => {
    saveAutoUpdateToStorage(autoUpdate);
  }, [autoUpdate]);

  const toggle = async () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    const newAutoUpdate = false; // Disable autoUpdate when manually toggling
    
    // Update state immediately for responsive UI
    setMode(newMode);
    setAutoUpdate(newAutoUpdate);
    
    // Save to localStorage immediately for persistence
    saveModeToStorage(newMode);
    saveAutoUpdateToStorage(newAutoUpdate);

    if (user && updateUserPreferences) {
      // Update user preferences in Firestore
      const newPreferences = {
        darkMode: newMode === 'dark',
        autoUpdate: newAutoUpdate,
        emailNotifications: user.preferences?.emailNotifications !== undefined ? user.preferences.emailNotifications : true,
      };

      // Save to Firestore (best effort, don't block UI)
      try {
        await updateUserPreferences(newPreferences);
        console.log('[ThemeProvider] Theme preferences saved to Firestore:', newPreferences);
      } catch (err) {
        console.error('[ThemeProvider] Failed to update theme preferences in Firestore:', err);
        // localStorage still has the preference, so UI will persist on reload
      }
    }
  };

  const value = useMemo(() => ({
    mode,
    palette,
    toggle,
    setMode,
    setAutoUpdate,
    autoUpdate,
  }), [mode, palette, autoUpdate]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}


